import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Clock, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatApiError } from '@/utils/apiError';

const LABEL = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };

export default function PostcodeNudgeCard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, checkAuth } = useAuth();
  const [state, setState] = useState(null);
  const [postcode, setPostcode] = useState('');
  const [radius, setRadius] = useState('30');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'installer' || (user?.postcode || '').trim()) { setState(null); return; }
    fetch(`${API_URL}/api/me/postcode-nudge`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then(setState).catch(() => setState(null));
  }, [isAuthenticated, user?.role, user?.postcode]);

  if (!state?.show) return null;

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/me/postcode-nudge/postcode`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcode, coverage_radius_miles: parseInt(radius, 10) || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d) || 'Could not save postcode');
      toast.success(`Postcode saved${d.town ? ` — ${d.town}` : ''}. You'll now be matched to nearby leads.`);
      setState(null);
      await checkAuth();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const snooze = async () => {
    setState(null);
    await fetch(`${API_URL}/api/me/postcode-nudge/snooze`, { method: 'POST', credentials: 'include' }).catch(() => {});
    toast.message("We'll remind you again in 7 days");
  };

  const approved = (state.approved_sections || []).map((s) => LABEL[s] || s).join(', ');

  return (
    <div className="mb-6 rounded-2xl border-2 border-amber-300 bg-amber-50 shadow-sm overflow-hidden" data-testid="postcode-nudge-card">
      <div className="p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="rounded-xl bg-amber-400/30 text-amber-800 p-2.5 shrink-0"><MapPin className="w-5 h-5" /></div>
          <div className="min-w-0">
            <h3 className="font-bold text-amber-900 text-base">Add your postcode to start receiving nearby leads</h3>
            <p className="text-sm text-amber-800/90 mt-0.5">
              You&apos;re Ox Approved for {approved || 'your sector'}, but we can&apos;t match you to customer enquiries or show you on the installer map until your Business Profile has a postcode. It stays private — we only use it to work out distance to a job.
            </p>
          </div>
        </div>
        <form onSubmit={save} className="flex flex-wrap items-center gap-2 shrink-0" data-testid="postcode-nudge-form">
          <Input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Your postcode" className="h-10 w-36 bg-white uppercase" required data-testid="postcode-nudge-input" />
          <div className="flex items-center gap-1 text-sm text-amber-900">
            <Input type="number" min="1" max="500" value={radius} onChange={(e) => setRadius(e.target.value)} className="h-10 w-20 bg-white" title="How far will you travel?" data-testid="postcode-nudge-radius" />
            <span className="text-xs">miles</span>
          </div>
          <Button type="submit" disabled={busy || !postcode.trim()} className="h-10 bg-[#006400] hover:bg-[#005000] text-white" data-testid="postcode-nudge-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add postcode'}
          </Button>
        </form>
      </div>
      <div className="px-4 sm:px-5 py-2 bg-amber-100/70 flex flex-wrap items-center justify-between gap-2 text-xs">
        <button type="button" onClick={() => navigate('/settings')} className="text-amber-900 font-semibold underline" data-testid="postcode-nudge-settings-link">Open Business Profile instead</button>
        <button type="button" onClick={snooze} className="inline-flex items-center gap-1 text-amber-800 hover:text-amber-950" data-testid="postcode-nudge-snooze">
          <Clock className="w-3.5 h-3.5" /> Remind me later <X className="w-3 h-3 ml-1" />
        </button>
      </div>
    </div>
  );
}
